import colors from '@/colors-palette';
import { PostStage } from '@/graphql/generated/types.generated';
import {
    AutoAwesomeRounded,
    BrushRounded,
    ErrorOutlineRounded,
    EventAvailableRounded,
    FlipCameraAndroidRounded,
    HourglassTopRounded,
    SvgIconComponent,
    TaskAltRounded,
    VerifiedRounded,
} from '@mui/icons-material';
import { Avatar, Icon, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

export const stageAvatarProps = (stage: PostStage, isDarkMode = false) => {
    const map: Record<
        PostStage,
        { color: string; icon: SvgIconComponent; strKey: string; chipColor: string }
    > = {
        IDEA: {
            color: colors.pink[500],
            icon: AutoAwesomeRounded,
            strKey: 'post.status.idea',
            chipColor: colors.pink[100],
        },
        DRAFT: {
            color: colors.blueGrey[600],
            icon: BrushRounded,
            strKey: 'post.status.draft',
            chipColor: colors.blueGrey[100],
        },
        TEAM_REVIEW: {
            color: colors.blue[500],
            icon: HourglassTopRounded,
            strKey: 'post.status.team-review',
            chipColor: colors.blue[100],
        },
        TEAM_REVISION: {
            color: colors.blue[600],
            icon: FlipCameraAndroidRounded,
            strKey: 'post.status.team-revision',
            chipColor: colors.blue[100],
        },
        TEAM_APPROVED: {
            color: colors.blue[700],
            icon: TaskAltRounded,
            strKey: 'post.status.team-approved',
            chipColor: colors.blue[100],
        },
        CLIENT_REVIEW: {
            color: colors.deepPurple[400],
            icon: HourglassTopRounded,
            strKey: 'post.status.client-review',
            chipColor: colors.deepPurple[100],
        },
        CLIENT_REVISION: {
            color: colors.deepPurple[600],
            icon: FlipCameraAndroidRounded,
            strKey: 'post.status.client-revision',
            chipColor: colors.deepPurple[100],
        },
        CLIENT_APPROVED: {
            color: colors.deepPurple[700],
            icon: TaskAltRounded,
            strKey: 'post.status.client-approved',
            chipColor: colors.deepPurple[100],
        },
        SCHEDULED: {
            color: colors.green[isDarkMode ? 400 : 500],
            icon: EventAvailableRounded,
            strKey: 'post.status.scheduled',
            chipColor: colors.green[isDarkMode ? 50 : 100],
        },
        PUBLISHED: {
            color: colors.green[isDarkMode ? 500 : 700],
            icon: VerifiedRounded,
            strKey: 'post.status.published',
            chipColor: colors.green[100],
        },
        PUBLISHED_FAILED: {
            color: colors.red[600],
            icon: ErrorOutlineRounded,
            strKey: 'post.status.publish-failed',
            chipColor: colors.red[100],
        },
        // This stage it's not supposed to happen because in BE we only fetch posts that is not archived
        ARCHIVE: {
            color: '',
            icon: ErrorOutlineRounded,
            strKey: '',
            chipColor: '',
        },
    };

    return map[stage];
};

type PostStageAvatarProps = {
    stage: PostStage;
    chip?: boolean;
    noAvatar?: boolean;
    noLabel?: boolean;
    vertical?: boolean;
};
const PostStageAvatar = ({ stage, chip, noAvatar, noLabel, vertical }: PostStageAvatarProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { icon, color, strKey, chipColor } = stageAvatarProps(stage, theme.palette.mode === 'dark');

    return (
        <Stack
            direction="row"
            alignItems="center"
            width="max-content"
            gap={1}
            {...(chip && {
                borderRadius: 1,
                p: (theme) => (vertical ? theme.spacing(1, 0.5) : theme.spacing(0.5, 1)),
                bgcolor: chipColor,
            })}
            data-id="post-planning-stage"
            data-stage={stage}
        >
            {!noAvatar && (
                <Avatar
                    variant="rounded"
                    sx={{
                        width: 18,
                        height: 18,
                        bgcolor: color,
                    }}
                >
                    <Icon sx={{ width: 16, height: 16, color: '#E1E3F0' }} component={icon} />
                </Avatar>
            )}
            {!noLabel && (
                <Typography color={color} fontWeight={500} fontSize={14}>
                    {t(strKey)}
                </Typography>
            )}
        </Stack>
    );
};
export default PostStageAvatar;
